import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuthStore } from "@/stores/authStore";
import { fetchCsrfToken } from "@/api/csrf";

export const EXPORT_FORMATS = ["csv", "xlsx", "pdf"];
const EXCLUDED_COLUMN_IDS = ["select", "actions", "action", "view"];
const BRAND_COLOR = "#005f8d";

const slugify = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const timestamp = () =>
  new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "-");

const columnLabel = (column) => {
  const { label, header } = column.columnDef ?? {};
  if (label) return label;
  if (typeof header === "function") return header();
  return typeof header === "string" ? header : column.id;
};

const getNested = (obj, path) =>
  String(path ?? "")
    .split(".")
    .reduce((acc, part) => acc?.[part], obj);

const resolveValue = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value.map(resolveValue).filter((v) => v !== "").join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    const name =
      value.name ?? value.title ?? value.fullname ?? value.label ?? value.id;
    return name != null && name !== "" ? String(name) : JSON.stringify(value);
  }
  return String(value);
};

/** Visible, exportable leaf columns of a TanStack table. */
export const exportableColumns = (table) =>
  table
    .getAllLeafColumns()
    .filter((c) => c.getIsVisible() && !EXCLUDED_COLUMN_IDS.includes(c.id));

/** Turns raw items (or TanStack rows) into { labels, rows: string[][] }. */
export function buildExportTable(columns, sourceRows) {
  const labels = columns.map(columnLabel);
  const rows = sourceRows.map((row, index) => {
    const item = row?.original ?? row;
    return columns.map((column) => {
      const def = column.columnDef ?? {};
      let raw;
      if (def.meta?.exportFormatter) raw = def.meta.exportFormatter(item);
      else if (typeof def.accessorFn === "function") {
        try {
          raw = def.accessorFn(item, index);
        } catch {
          raw = "";
        }
      } else raw = getNested(item, def.accessorKey ?? column.id);
      return resolveValue(raw);
    });
  });
  return { labels, rows };
}

export function downloadBlob(blob, fileName) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

const generatedAt = () =>
  new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

function exportCsv({ title, companyName, labels, rows, fileBase }) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [esc(title)];
  if (companyName) lines.push(esc(companyName));
  lines.push(esc(`Generated: ${generatedAt()}`), "");
  lines.push(labels.map(esc).join(","));
  rows.forEach((row) => lines.push(row.map(esc).join(",")));
  const blob = new Blob([`﻿${lines.join("\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  downloadBlob(blob, `${fileBase}.csv`);
}

async function exportXlsx({ title, labels, rows, fileBase }) {
  const base = import.meta.env.VITE_API ?? "";
  const token = useAuthStore.getState().token;
  const csrfToken = await fetchCsrfToken();
  const res = await fetch(`${base}reports/export-table`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
    },
    body: JSON.stringify({ title, tabName: null, columns: labels, rows }),
  });
  if (!res.ok) throw new Error(`Table export failed (${res.status})`);
  downloadBlob(await res.blob(), `${fileBase}.xlsx`);
}

function exportPdf({ title, companyName, labels, rows, fileBase }) {
  const doc = new jsPDF();
  let y = 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(BRAND_COLOR);
  doc.text(title, 14, y);
  if (companyName) {
    y += 6;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(companyName, 14, y);
  }
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text(`Generated: ${generatedAt()}`, 14, y);

  const chunkSize = 4;
  for (let i = 0; i < labels.length; i += chunkSize) {
    autoTable(doc, {
      head: [labels.slice(i, i + chunkSize)],
      body: rows.map((row) => row.slice(i, i + chunkSize)),
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 8,
      styles: { fontSize: 10 },
      headStyles: { fillColor: BRAND_COLOR, textColor: "#ffffff", fontStyle: "bold" },
    });
  }
  doc.save(`${fileBase}.pdf`);
}

/**
 * Exports table rows in the given format.
 * @param {{ format: "csv"|"xlsx"|"pdf", title: string, columns: Column[], sourceRows: any[], companyName?: string }} args
 */
export async function exportTable({ format, title, columns, sourceRows, companyName }) {
  const { labels, rows } = buildExportTable(columns, sourceRows);
  const payload = {
    title,
    companyName,
    labels,
    rows,
    fileBase: `${slugify(title) || "table"}_${timestamp()}`,
  };
  if (format === "csv") return exportCsv(payload);
  if (format === "xlsx") return exportXlsx(payload);
  if (format === "pdf") return exportPdf(payload);
  throw new Error(`Unsupported export format: ${format}`);
}
